import React, { useRef, useState, useEffect } from "react";
import { injectIntl } from "react-intl";
import { reduxForm, change, formValueSelector } from "redux-form";
import Flatpickr from "react-flatpickr";
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import { useDispatch, useSelector } from "react-redux";
import moment from "moment-timezone";
import withStyles from "isomorphic-style-loader/withStyles";
import cx from "classnames";

import Loader from "../Common/Loader/Loader";

import { openHeaderModal } from "../../actions/siteadmin/modalActions";
import { closeHeaderModal } from "../../actions/siteadmin/modalActions";
import { createScheduleBooking } from "../../actions/booking/createScheduleBooking";
import { updateScheduleBooking } from "../../actions/siteadmin/ManualBooking/updateScheduleBooking";
import { readBookingDraft, writeBookingDraft } from "../../helpers/bookingDraft";
import messages from "../../locale/messages";

import { flatPickerLocale } from "../../helpers/flatPickerLocale";
import showToaster from "../../helpers/toastrMessgaes/showToaster";
import defaultDateAndTimeIcon from '/public/Icons/defaultDateAndTimeIcon.svg';
import n from "flatpickr/dist/themes/material_green.css";
import bt from '../../components/commonStyle.css';
import r from '../../styles/flatPickerCalendar.css';
import s from './ScheduleBookingForm.css';

function ScheduleBookingForm({ intl: { formatMessage, locale }, formData }) {

    const [showDate, setShowDate] = useState('');


    const dispatch = useDispatch();
    const flatPickerRef = useRef(null);

    const loading = useSelector(state => state.loader.scheduleLoading)
    const scheduleLoading = useSelector(state => state.booking.scheduledLoading)
    const isPromoIdRemove = useSelector(state => formValueSelector('PromoCodeForm')(state, 'isPromoIdRemove'))
    const promoId = useSelector(state => formValueSelector('PromoCodeForm')(state, 'promoId'))
    const paymentType = useSelector(state => formValueSelector('PaymentMethod')(state, 'paymentMethodId'))
    const description = useSelector(state => formValueSelector('OrderDetails')(state, 'description'))
    const questionnaire = useSelector(state => formValueSelector('OrderDetails')(state, 'questionnaire'))
    const estimatedHours = useSelector(state => formValueSelector('OrderDetails')(state, 'estimatedHours'))
    const scheduleDate = useSelector(state => formValueSelector('ScheduleBookingForm')(state, 'scheduleDate'))
    const validPromoId = !isPromoIdRemove ? (promoId ? promoId : formData?.bookingData?.promoId) : null
    const scheduleFrom = formData?.scheduleFrom;

    const scheduleId = formData?.scheduleId;
    // Resumable booking: only the customer order flow (no admin scheduleId) persists its date, keyed by
    // the order id.
    const draftOrderId = !scheduleId ? formData?.bookingData?.id : null;

    useEffect(() => {
        // Prefer a previously persisted date (refresh / Back / post-login return) over the default seed.
        const draft = readBookingDraft(draftOrderId);
        const initial = draft.scheduleDate ? new Date(draft.scheduleDate) : scheduleFrom;
        dispatch(change('ScheduleBookingForm', 'scheduleDate', initial));
    }, []);

    useEffect(() => {
        if (scheduleDate) {
            setShowDate(moment(scheduleDate).locale(locale).format(`ddd, MMM D, [${formatMessage(messages.at)}] h:mma`));
            if (draftOrderId) writeBookingDraft(draftOrderId, { scheduleDate: moment(scheduleDate).valueOf() });
        }
    }, [scheduleDate, locale, formatMessage]);

    const changeDate = (dates) => {
        if (dates && dates[0]) {
            const selectedDate = moment(dates[0])
            const minimumTime = moment().add(20, 'minutes').toDate()
            if (selectedDate.isBefore(minimumTime)) {
                showToaster({ messageId: 'requestDate', toasterType: 'error' });
                dispatch(change('ScheduleBookingForm', 'scheduleDate', dates[0]));
                setShowDate(moment(dates[0]).locale(locale).format(`ddd, MMM D, [${formatMessage(messages.at)}] h:mma`));
            } else {
                dispatch(change('ScheduleBookingForm', 'scheduleDate', dates[0]));
                setShowDate(moment(dates[0]).locale(locale).format(`ddd, MMM D, [${formatMessage(messages.at)}] h:mma`));
            }
        }
    }
    const closeScheduledBookingModal = () => {
        dispatch(closeHeaderModal('scheduleBookingForm'));
        dispatch(openHeaderModal('bookingForm'));
    }
    const submitDate = async () => {
        if (!scheduleDate) {
            return showToaster({ messageId: 'selectData', toasterType: 'error' });
        }
        let scheduleFrom = moment(scheduleDate);

        scheduleFrom = moment(scheduleFrom).unix();
        const trimmedDescription = description?.trim() === "" ? null : description;
        const bookingData = formData?.bookingData;
        const orderItem = bookingData?.orderItemsList?.[0];

        const orderId = bookingData?.id;
        const dropOffLocation = orderItem?.dropLocation;
        const dropOffLat = orderItem?.dropLat;
        const dropOffLng = orderItem?.dropLng;
        const requestCurrency = bookingData?.currency;

        const bookingId = formData?.bookingId, isUserBooking = formData?.isUserBooking,
            userId = formData?.userId, currency = formData?.currency, currentPage = formData?.currentPage;

        if (!scheduleId) {
            // Parity with app: scheduled bookings run through the unified engine (broadcast = "Choose a
            // pro for me"); carry the questionnaire + duration like an immediate booking.
            await dispatch(createScheduleBooking({ orderId, dropOffLocation, dropOffLat, dropOffLng, requestCurrency, promoId: validPromoId, reviewDescription: trimmedDescription, paymentType, scheduleFrom, dispatchMode: 'auto', questionnaire: questionnaire || null, estimatedHours: estimatedHours || null }))
        } else {
            await dispatch(updateScheduleBooking({ bookingId, userId, scheduleId, scheduleFrom, requestCurrency: currency, currentPage, isUserBooking }))
        }
        dispatch(change('ScheduleBookingForm', 'scheduleFrom', scheduleFrom));
        dispatch(closeHeaderModal('scheduleBookingForm'));
    }
    const openCalendar = () => {
        if (flatPickerRef.current) {
            flatPickerRef.current.open();
        }
    };

    return (
        <div>
            <>
                <Form.Group className={bt.space2}>
                    <label className={bt.commonLabelText} htmlFor="my-date-picker">{formatMessage(messages.dateAndTime)}</label>
                    <div className='scheduleBookingDateTimeContainer'>
                        {scheduleDate && <Flatpickr
                            id="my-date-picker"
                            data-enable-time
                            options={{
                                enableTime: true,
                                monthSelectorType: "static",
                                disableMobile: 'true',
                                time_24hr: false,
                                static: true,
                                minDate: new Date(),
                                maxDate: moment().add(31, 'day').toDate(),
                                dateFormat: 'Y-m-d, h:i K',
                                locale: locale ? flatPickerLocale[locale] : flatPickerLocale['en-US']
                            }}
                            onClose={changeDate}
                            value={new Date(scheduleDate)}
                            onReady={(_, __, fpInstance) => {
                                flatPickerRef.current = fpInstance;
                            }}

                        />}
                        <Button onClick={openCalendar} aria-label={formatMessage(messages.chooseDateTime)} className={cx(s.scheduleClockBtn, 'scheduleClockBtnRtl')}>
                            <img src={defaultDateAndTimeIcon} alt="" />
                        </Button>
                    </div>
                </Form.Group>
                <span className={cx(bt.commonSize14, bt.fontWeight400, bt.primaryColor)}>{showDate && `${formatMessage(messages.bookingScheduleOn)} ${showDate}`}</span>
            </>
            <div className={cx('buttonGroupContainer', 'buttonGroupDivider')}>
                <Button onClick={() => closeScheduledBookingModal()} className={bt.btnSecondary}>
                    {formatMessage(messages.cancelButton)}
                </Button>
                <Loader
                    type={"button"}
                    buttonType={'button'}
                    show={loading || scheduleLoading}
                    handleClick={submitDate}
                    label={formatMessage(messages.confirmBooking)}
                    className={cx(bt.btnPrimary, 'loaderButtonFlex')}
                />
            </div>
        </div >
    )
}
ScheduleBookingForm = reduxForm({
    form: 'ScheduleBookingForm',
})(ScheduleBookingForm);

export default injectIntl(withStyles(n, bt, r, s)(ScheduleBookingForm))